import React from 'react'
import { useLocation } from 'react-router-dom'
import useSWR from 'swr'

import { getZrivePrograms } from '../../api/zrive'
import {
  sendZriveReferral,
  getMyZriveReferralSignups,
  getMyZriveReferralRewards,
  claimZriveReferralReward,
  type ZriveReferralRewardListItem,
} from '../../api/users'
import { ReferralRewardCode } from '../../constants/referral-reward-codes'
import { CurrentUserContext } from '../../libraries/auth'
import FormGroup from '../components/form/FormGroup'
import TextInput from '../components/form/TextInput'
import SelectInput from '../components/form/SelectInput'
import type { OptionType } from '../components/form/SelectInput'
import Button from '../components/form/Button'
import Loading from '../components/layout/Loading'
import { useI18N } from '../../libraries/i18n/hook'
import ErrorMessage from '../components/errors/ErrorMessage'
import PremiumTag from '../components/premium/Tag'
import AmazonLogo from '../../assets/amazon-logo.png'
import Translate from '../../libraries/i18n/Translate'

function formatRewardDate (iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function rewardDescriptionI18nKey (rewardName: string): 'zriveReferralProgram.description1' | 'zriveReferralProgram.description2' {
  return rewardName === ReferralRewardCode.FREE_BLOOM_PRO ? 'zriveReferralProgram.description1' : 'zriveReferralProgram.description2'
}

const ZriveReferralProgram = () => {
  const { t } = useI18N()
  const location = useLocation()
  const { refetchCurrentUser } = React.useContext(CurrentUserContext)
  const [referredName, setReferredName] = React.useState('')
  const [referredEmail, setReferredEmail] = React.useState('')
  const [zriveProgramId, setZriveProgramId] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)
  const [claimingId, setClaimingId] = React.useState<number | null>(null)
  const [rewardSuccessMessage, setRewardSuccessMessage] = React.useState<string | null>(null)
  const [claimError, setClaimError] = React.useState<{ rewardId: number; message: string } | null>(null)

  const { data: programs, isLoading: programsLoading } = useSWR('zrive/programs', getZrivePrograms)
  const { data: myReferrals, mutate: mutateMyReferrals } = useSWR('users/zrive-referral-signup', getMyZriveReferralSignups)
  const { data: rewards = [], mutate: mutateRewards } = useSWR('users/zrive-referral-signup/rewards', getMyZriveReferralRewards)
  const joinedCount = myReferrals?.filter(
    (r) => r.referredJoinedZrive && r.zriveProgram?.enableForReferralProgram !== false,
  ).length ?? 0

  const sortedRewards: ZriveReferralRewardListItem[] = [...rewards].sort((a, b) => {
    if (a.claimed !== b.claimed) return a.claimed ? 1 : -1
    const dateA = new Date(a.claimed && a.claimedAt ? a.claimedAt : a.updatedAt).getTime()
    const dateB = new Date(b.claimed && b.claimedAt ? b.claimedAt : b.updatedAt).getTime()
    return dateB - dateA
  })

  const handleClaim = async (item: ZriveReferralRewardListItem) => {
    setClaimingId(item.id)
    setRewardSuccessMessage(null)
    setClaimError(null)
    try {
      await claimZriveReferralReward(item.id)
      void mutateRewards()
      const message =
        item.reward.name === ReferralRewardCode.FREE_BLOOM_PRO
          ? t('zriveReferralProgram.claimSuccessBloom')
          : t('zriveReferralProgram.claimSuccessAmazon')
      setRewardSuccessMessage(message)
      if (item.reward.name === ReferralRewardCode.FREE_BLOOM_PRO) {
        await refetchCurrentUser()
      }
    } catch (err) {
      setClaimError({ rewardId: item.id, message: err instanceof Error ? err.message : 'Failed to claim reward.' })
    } finally {
      setClaimingId(null)
    }
  }

  const programOptions: OptionType[] = (programs ?? []).map((p) => ({
    label: p.longName ?? p.name,
    value: String(p.id),
  }))

  const selectedOption = programOptions.find((o) => o.value === zriveProgramId)
  const selectedProgram = programs?.find((p) => String(p.id) === zriveProgramId)
  const isProgramNotPartOfReferral = selectedProgram?.enableForReferralProgram === false

  React.useEffect(() => {
    if (programsLoading || location.hash !== '#rewards') return

    document.getElementById('rewards')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.hash, programsLoading])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const email = referredEmail.trim().toLowerCase()
    const programId = zriveProgramId ? Number(zriveProgramId) : 0
    if (!email || !programId) {
      setError(t('zriveReferralProgram.validation.required') ?? 'Email and program are required.')
      return
    }
    setSubmitting(true)
    try {
      await sendZriveReferral({
        referredEmail: email,
        referredName: referredName.trim() || undefined,
        zriveProgramId: programId,
      })
      setSuccess(true)
      setReferredName('')
      setReferredEmail('')
      setZriveProgramId('')
      void mutateMyReferrals()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation.')
    } finally {
      setSubmitting(false)
    }
  }

  if (programsLoading) return <Loading />

  return (
    <div className='min-h-screen bg-gray-50/80 p-2 sm:py-6'>
      <div className='max-w-6xl mx-auto w-full'>
        <h1 className='text-center text-3xl sm:text-4xl font-bold text-deep-green mb-8'>
          {t('zriveReferralProgram.title')}
        </h1>

        <div className='flex flex-col items-center sm:items-stretch sm:flex-row gap-6 sm:gap-6 mx-auto justify-center'>
          {/* Benefits section - 70% on sm+ */}
          <section className='w-full max-w-md shrink-0 rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden'>
          <div className='grid divide-y sm:divide-y-0 sm:divide-x divide-gray-100'>
            <div className='p-6 sm:p-4'>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-deep-green/80 mb-4'>
                {t('zriveReferralProgram.theyGet')}
              </h2>
              <ul className='space-y-3 text-gray-700'>
                <li>
                  <Translate i18nKey='zriveReferralProgram.theyGet.description1'><b /></Translate>
                </li>
              </ul>
            </div>
            <div className='p-6 sm:p-4'>
              <h2 className='text-sm font-semibold uppercase tracking-wide text-deep-green/80 mb-4'>
                {t('zriveReferralProgram.youGet')}
              </h2>
              <ul className='space-y-4 text-gray-700'>
                <li className='flex items-start gap-3'>
                  <span className='shrink-0 mt-0.5'><PremiumTag size='sm' /></span>
                  <span><Translate i18nKey='zriveReferralProgram.description1'><b /></Translate></span>
                </li>
                <li className='flex items-start gap-3'>
                  <img src={AmazonLogo} alt='' className='shrink-0 w-20 h-8 object-contain object-left' />
                  <span><Translate i18nKey='zriveReferralProgram.description2'><b /></Translate></span>
                </li>
              </ul>
              <div className='mt-6 max-w-64 block text-center mx-auto rounded-xl border-2 border-electric-green bg-electric-green/10 px-6 py-4'>
                <div><span className='text-xs font-medium text-deep-green'>{t('zriveReferralProgram.referralsCompleted')}</span></div>
                <div><span className='text-3xl font-bold text-deep-green tabular-nums'>{joinedCount}</span></div>
              </div>
            </div>
          </div>
          </section>

          {/* Invite form section - 30% on sm+ */}
          <section className='w-full sm:w-[40%] shrink-0 rounded-2xl border border-gray-200/80 bg-white shadow-sm px-6 sm:px-8'>
          {success ? (
            <div className='rounded-xl bg-electric-green/10 border border-electric-green p-6 text-center'>
              <p className='text-deep-green font-semibold'>
                {t('zriveReferralProgram.success') ?? 'Invitation sent! Your friend will receive an email.'}
              </p>
              <Button variant='primary' size='md' className='mt-4' onClick={() => setSuccess(false)}>
                {t('zriveReferralProgram.sendAnother') ?? 'Send another invitation'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='pb-4'>
              <FormGroup>
                <TextInput
                  label={t('zriveReferralProgram.friendName')}
                  name='referredName'
                  value={referredName}
                  onChange={(e) => setReferredName(e.target.value)}
                  placeholder={t('zriveReferralProgram.friendName')}
                  className='w-full border-gray-200/70 border'
                />
              </FormGroup>
              <FormGroup>
                <TextInput
                  label={t('zriveReferralProgram.friendEmail')}
                  name='referredEmail'
                  type='email'
                  value={referredEmail}
                  onChange={(e) => setReferredEmail(e.target.value)}
                  placeholder={t('zriveReferralProgram.friendEmail')}
                  className='w-full border-gray-200/70 border'
                  required
                />
              </FormGroup>
              <FormGroup>
                <SelectInput
                  name='zriveProgramId'
                  label={t('zriveReferralProgram.program') ?? 'Zrive program'}
                  options={programOptions}
                  value={selectedOption}
                  onChange={(value) => setZriveProgramId(value)}
                  isMulti={false}
                  required
                  showBorder
                />
                {isProgramNotPartOfReferral && (
                  <p className='mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'>
                    {t('zriveReferralProgram.programNotPartOfReferral')}
                  </p>
                )}
              </FormGroup>
              {error && <ErrorMessage message={error} />}
              <div className='pt-2 text-center'>
                <Button type='submit' variant='primary' size='md' disabled={submitting} loading={submitting} className='w-full sm:w-auto min-w-48'>
                  {t('zriveReferralProgram.submit') ?? 'Send invitation'}
                </Button>
              </div>
              <div className='text-center pt-4'><small>Se aplican <a className='text-deep-green underline' target='_blank' rel='noopener noreferrer' href='https://zrive.notion.site/T-rminos-y-condiciones-del-Referral-program-de-Zrive-3193a00aa35d80b7a65ed70dc5a047a6?pvs=74'>Terminos y Condiciones</a></small></div>
            </form>
          )}
          </section>
        </div>

        {/* My Rewards */}
        <section id='rewards' className='mt-10 w-full max-w-4xl mx-auto rounded-2xl border border-gray-200/80 bg-white shadow-sm p-6 sm:p-8'>
          <h2 className='text-lg font-semibold text-deep-green mb-4'>
            {t('zriveReferralProgram.myRewards')}
          </h2>
          {rewardSuccessMessage && (
            <div className='mb-4 rounded-xl bg-electric-green/10 border border-electric-green px-4 py-3 text-deep-green text-sm'>
              {rewardSuccessMessage}
            </div>
          )}
          {sortedRewards.length === 0 ? (
            <p className='text-gray-600 text-sm'>{t('zriveReferralProgram.noRewardsYet')}</p>
          ) : (
            <ul className='space-y-4'>
              {sortedRewards.map((item) => {
                const displayDate = item.claimed && item.claimedAt ? item.claimedAt : item.updatedAt
                const i18nKey = rewardDescriptionI18nKey(item.reward.name)
                return (
                  <li
                    key={item.id}
                    className='py-3 border-b border-gray-100 last:border-0'
                  >
                    <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4'>
                      <span className='text-gray-500 text-sm w-full sm:w-auto sm:shrink-0'>
                        {formatRewardDate(displayDate)}
                      </span>
                      <div className='flex gap-3 items-center w-full sm:flex-1 sm:min-w-0'>
                        <span className='text-gray-700 flex-1 min-w-0'>
                          {item.reward.name === ReferralRewardCode.FREE_BLOOM_PRO && <span className='shrink-0 inline-block mr-1 mt-0.5 align-middle'><PremiumTag size='sm' /></span>}
                          {item.reward.name === ReferralRewardCode.AMAZON_TICKET && <img src={AmazonLogo} alt='' className='inline-block w-16 h-6 object-contain object-left mr-2 align-middle' />}
                          <Translate i18nKey={i18nKey}><b /></Translate>
                        </span>
                        <span className='shrink-0'>
                          {item.claimed ? (
                            <span className='text-deep-green font-medium text-sm'>{t('zriveReferralProgram.claimed')}</span>
                          ) : item.readyToClaim ? (
                            <Button
                              variant='primary'
                              size='sm'
                              disabled={claimingId !== null}
                              loading={claimingId === item.id}
                              onClick={() => void handleClaim(item)}
                            >
                              {t('zriveReferralProgram.claim')}
                            </Button>
                          ) : (
                            <span className='text-gray-500 text-sm'>
                              <Translate i18nKey='zriveReferralProgram.referralsNeeded' values={{ count: Math.max(0, item.reward.referralGoalAmount - item.referralCount) }} />
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    {claimError?.rewardId === item.id && (
                      <div className='mt-2'>
                        <ErrorMessage message={claimError.message} />
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

export default ZriveReferralProgram
